/**
 * Enemy-mechanic overlays (three.js): soak circles with soaker pips, tracking beams, spread /
 * explode countdown rings with an overhead marker, fixate and tether lines, growing poison
 * pools, wave fronts and the cracking shield bubble on shield-gated casters.
 *
 * Drawn on top of the base telegraph decals (areas.ts), which keep the shape and the fill.
 * Everything is pooled at construction (fixed mesh + material per slot, one shared ground
 * shader program and one bubble program); per frame only uniforms and transforms change.
 * Mark lifetimes run through the FadeTrackers in state.ts.
 */
import * as THREE from 'three';
import type { UnitId } from '@keystone/sim';
import type { RenderUnit } from '../../../types';
import { groundY } from '../../ground';
import { NOISE_GLSL, U } from '../../style';
import { HAZARD } from '../enemyTable';
import type { AreaMark, MechanicsFrame } from './model';
import {
  type Bubble,
  BubbleState,
  type CountdownLook,
  FadeTracker,
  type SoakLook,
  SlotState,
  type TetherLook,
  WavePhase,
  countdownLook,
  flourish,
  growingRadius,
  infoAlpha,
  newBubble,
  shatterFlash,
  soakLook,
  stepBubble,
  tetherLook,
  wavePhase,
  waveFront,
} from './state';

/** Ground overlay modes (uMode). */
const MODE = { soak: 0, beam: 1, pool: 2, wave: 3, ring: 4, link: 5, lane: 6, mover: 7, cone: 8 } as const;

const GROUND_VERT = `uniform vec2 uHalf; varying vec2 vLocal;
void main(){ vLocal = position.xy * uHalf; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`;

const GROUND_FRAG = `
uniform float uTime; uniform int uMode; uniform int uShape; uniform float uA; uniform float uB;
uniform vec4 uP; uniform vec4 uQ; uniform vec3 uColor; uniform vec3 uRim; uniform vec3 uHot;
uniform float uAlpha; uniform float uMotion; uniform float uSeed;
varying vec2 vLocal;
${NOISE_GLSL}
const float TAU = 6.2831853;
float rimOf(float e, float w){ return 1.0 - smoothstep(w * 0.6, w, e); }
void main(){
  vec2 p = vLocal; float d = length(p); float t = uTime + uSeed;
  vec3 col = uColor; float a = 0.0;
  if (uMode == 2) {
    // Growing poison pool: uP = (radius, max, growth yd/s, life).
    float r = uP.x;
    if (d > uP.y + 0.15) discard;
    if (d > r) {
      // Where it will grow to: faint dashed ring at max.
      float dash = step(0.5, fract(atan(p.y, p.x) * uP.y * 0.32 - t * 0.4 * uMotion));
      float ring = 1.0 - smoothstep(0.06, 0.14, abs(d - uP.y));
      a = ring * dash * 0.2 * step(0.01, uP.y - r);
      col = uRim;
    } else {
      float n = fbm(p * 0.55 + vec2(t * 0.07, -t * 0.05) * uMotion);
      float bub = smoothstep(0.78, 0.84, noise(p * 1.7 + vec2(uSeed, t * 0.5 * uMotion)));
      float rim = rimOf(r - d, 0.32);
      // Growth front: a ripple that keeps rolling into the rim while it spreads.
      float front = step(0.001, uP.z) * (1.0 - smoothstep(0.0, 0.18, abs((r - d) - 0.7 * (1.0 - fract(t * 1.2)))));
      a = 0.2 + n * 0.08 + bub * 0.1 * uMotion + rim * 0.22 + front * 0.12;
      col = mix(uColor * (0.75 + 0.35 * n), uHot, bub * 0.6 + front * 0.5);
      col = mix(col, uRim, rim * 0.6);
    }
    a *= mix(0.45, 1.0, clamp(uP.w * 4.0, 0.0, 1.0));
    gl_FragColor = vec4(min(col, vec3(1.0)), clamp(a * uAlpha, 0.0, 0.42));
    return;
  }
  if (uMode == 8) {
    // Cast cone from the caster (+x = facing): uP = (half angle, progress, safe wedge, deadly).
    if (d > uA) discard;
    float ang = abs(atan(p.y, p.x));
    float norm = d / uA;
    if (ang > uP.x) {
      // Safe wedge behind the caster (wide cones only): unlit, pale dashed edges.
      if (uP.z < 0.5) discard;
      float sEdge = min(uA - d, (ang - uP.x) * d);
      float sRim = (1.0 - smoothstep(0.1, 0.24, sEdge)) * step(0.5, fract(d * 0.7 - t * 0.8 * uMotion));
      float grow = smoothstep(0.0, 0.2, uP.y);
      col = mix(vec3(0.0), vec3(0.62, 0.68, 0.72), sRim);
      a = max(0.26 * (1.0 - sRim), sRim * 0.5) * grow;
      gl_FragColor = vec4(col, clamp(a * uAlpha, 0.0, 0.6));
      return;
    }
    float edgeD = min(uA - d, (uP.x - ang) * d);
    float rim = rimOf(edgeD, 0.26);
    float filled = 1.0 - step(uP.y, norm);
    float wave = (1.0 - smoothstep(0.0, 0.03, abs(norm - uP.y))) * step(0.02, uP.y);
    float pulse = 0.85 + 0.15 * sin(t * 6.2831853 * (1.0 + uP.w));
    a = 0.06 + filled * 0.14 + wave * 0.16 + rim * 0.62 * pulse;
    col = mix(uColor, uRim, max(rim, wave * 0.7));
    gl_FragColor = vec4(min(col, vec3(1.0)), clamp(a * uAlpha, 0.0, 0.6));
    return;
  }
  if (uMode == 7) {
    // Moving hazard: uP = (radius, spin, speed yd/s, 0). Local +x = direction of travel.
    float r = uP.x;
    if (d > uA) discard;
    float spd = clamp(uP.z / 8.0, 0.0, 1.0);
    // Wake: a tapering streak behind it, so the heading reads at a glance.
    float wakeW = r * (0.85 + p.x / (uA * 1.4));
    float wake = step(p.x, 0.0) * (1.0 - smoothstep(wakeW * 0.6, wakeW, abs(p.y))) * (1.0 - smoothstep(r, uA, -p.x)) * step(r * 0.95, d);
    float streak = step(0.55, fract(p.x * 0.9 + t * 3.0 * uMotion));
    // Heading chevron just ahead of the rim.
    float cx = r + 0.35 + r * 0.25;
    float chev = (1.0 - smoothstep(0.1, 0.2, abs((p.x - cx) + abs(p.y) * 0.85))) * step(abs(p.y), r * 0.7) * step(r, p.x);
    // Spinning blades inside (axes / vortices).
    float ang = atan(p.y, p.x) + t * 7.0 * uMotion;
    float blades = uP.y * smoothstep(0.55, 0.85, cos(ang * 3.0)) * smoothstep(r * 0.15, r * 0.35, d) * (1.0 - smoothstep(r * 0.82, r * 0.98, d));
    float rim = (1.0 - smoothstep(0.0, 0.14, abs(d - r)));
    a = wake * (0.1 + 0.08 * streak) * spd + chev * 0.5 * step(0.05, spd) + blades * 0.32 + rim * 0.55;
    col = mix(uColor, uRim, max(rim, chev));
    col = mix(col, uHot, blades * 0.6);
    gl_FragColor = vec4(min(col, vec3(1.0)), clamp(a * uAlpha, 0.0, 0.7));
    return;
  }
  float edgeD; float norm; float ang01;
  if (uShape == 0) {
    if (d > uA || d < uB) discard;
    edgeD = min(uA - d, uB > 0.0 ? d - uB : 1e3);
    norm = (d - uB) / max(uA - uB, 0.01);
    ang01 = fract(0.25 - atan(p.y, p.x) / TAU);  // clockwise from local +y
  } else {
    vec2 q = abs(p);
    if (q.x > uA || q.y > uB) discard;
    edgeD = min(uA - q.x, uB - q.y);
    norm = (p.x + uA) / (2.0 * uA);
    ang01 = norm;
  }
  if (uMode == 0) {
    // Soak: uP = (pips, filled, progress, urgency), uQ.x = satisfied.
    float pulse = 0.75 + 0.25 * sin(t * TAU * 3.0);
    float rim = rimOf(edgeD, 0.3);
    float stripes = step(0.55, fract((p.x + p.y) * 0.8 - t * 0.25 * uMotion));
    float band = smoothstep(0.32, 0.38, edgeD) * (1.0 - smoothstep(0.56, 0.62, edgeD));
    float sweep = band * step(ang01, uP.z);
    vec3 rimC = mix(uRim, vec3(0.92, 0.22, 0.12), uP.w);
    rimC = mix(rimC, uHot, uQ.x * 0.35);
    float rimA = rim * mix(0.7, 0.85 * pulse, uP.w);
    a = max(max(0.06 + stripes * 0.1, rimA), sweep * 0.32);
    col = mix(uColor, rimC, max(rim, sweep));
    // Soaker pips: a row across the centre; filled = someone eligible is inside.
    float n = uP.x; float spacing = 0.95;
    for (int i = 0; i < 5; i++) {
      if (float(i) >= n) break;
      vec2 c = vec2((float(i) - (n - 1.0) * 0.5) * spacing, 0.0);
      float pd = length(p - c);
      float filled = step(float(i) + 0.5, uP.y);
      float disc = 1.0 - smoothstep(0.26, 0.32, pd);
      float ring = (1.0 - smoothstep(0.3, 0.36, pd)) * smoothstep(0.18, 0.24, pd);
      float pip = max(disc * filled, ring);
      if (pip > 0.0) { col = mix(col, filled > 0.5 ? uHot : rimC, pip); a = max(a, pip * 0.9); }
    }
  } else if (uMode == 1) {
    // Tracking beam: hot core, flowing chevrons away from the caster, crisp long edges.
    float core = exp(-pow(abs(p.y) / max(uB * 0.32, 0.05), 2.0));
    float chev = step(0.62, fract((p.x - abs(p.y) * 0.9) * 0.45 - t * 1.8 * uMotion)) * smoothstep(0.1, 0.5, uB - abs(p.y));
    float rim = rimOf(uB - abs(p.y), 0.22);
    float src = 1.0 - smoothstep(0.0, 1.6, p.x + uA);
    a = 0.12 + core * 0.3 + chev * 0.1 + rim * 0.3 + src * 0.15;
    col = mix(uColor, uRim, max(core, src * 0.8));
  } else if (uMode == 3) {
    // Wave band: uP = (phase, front, flash).
    float rim = rimOf(edgeD, 0.22);
    if (uP.x < 0.5) {
      float dash = step(0.5, fract(ang01 * (uShape == 0 ? uA * 2.2 : uA * 0.6) - t * 0.5 * uMotion));
      a = rim * (0.22 + 0.18 * dash);
      col = uRim;
    } else if (uP.x < 1.5) {
      float f = uP.y;
      float pos = uShape == 0 ? (d - uB) / max(uA - uB, 0.01) : norm;
      float line = 1.0 - smoothstep(0.0, 0.08, abs(pos - f));
      float behind = step(pos, f);
      a = max(max(0.12 + behind * 0.12, rim * 0.6), line * 0.55);
      col = mix(uColor, uHot, max(line, rim * 0.5));
    } else {
      a = 0.36 * uP.z + rim * 0.4 * uP.z;
      col = mix(uRim, uHot, uP.z);
    }
  } else if (uMode == 4) {
    // Spread / explode ring on a player: uP = (sweep, pulseHz, urgent, kind 0 spread / 1 isolate / 2 explode).
    float pulse = 0.72 + 0.28 * sin(t * TAU * uP.y);
    float rim = rimOf(edgeD, 0.24);
    float dash = uP.w > 0.5 && uP.w < 1.5 ? step(0.4, fract(ang01 * uA * 1.6)) : 1.0;
    float band = smoothstep(0.3, 0.36, edgeD) * (1.0 - smoothstep(0.5, 0.56, edgeD));
    float arc = band * step(ang01, uP.x);
    vec3 rimC = mix(uRim, uHot, uP.z);
    a = max(max(0.05, rim * dash * 0.75 * pulse), arc * 0.45);
    col = mix(uColor, rimC, max(rim, arc));
  } else if (uMode == 6) {
    // Charge lane: uP = (progress, length, deadly, 0). Chevrons rush toward the far end; the lane
    // fills from the caster as the wind-up runs out; the impact end is a bright bar.
    float rim = rimOf(uB - abs(p.y), 0.22);
    float chev = step(0.6, fract((p.x - abs(p.y) * 1.1) * 0.35 - t * (1.2 + 2.0 * uP.x) * uMotion)) * smoothstep(0.15, 0.6, uB - abs(p.y));
    float endBar = 1.0 - smoothstep(0.0, 0.35, uA - p.x);
    a = 0.06 + chev * 0.16 + rim * 0.5 + endBar * 0.4;
    col = mix(uColor, uRim, max(rim, chev * 0.6));
    col = mix(col, uHot, endBar * 0.7 + uP.z * rim * 0.3);
  } else {
    // Link (fixate / tether): uP = (strain, kind 0 fixate / 1 tether, snap flash, length).
    float across = 1.0 - smoothstep(0.45, 1.0, abs(p.y) / max(uB, 0.01));
    if (uP.y < 0.5) {
      float dash = step(0.45, fract(p.x * 0.7 - t * 2.4 * uMotion));
      a = across * (0.25 + 0.4 * dash);
      col = mix(uColor, uRim, dash * 0.6);
    } else {
      float flick = uP.x > 0.85 ? 0.75 + 0.25 * sin(t * 40.0) : 1.0;
      a = across * (0.45 + 0.2 * uP.x) * flick + uP.z * 0.5 * across;
      col = mix(uColor, uHot, max(uP.x * uP.x, uP.z));
    }
  }
  gl_FragColor = vec4(min(col, vec3(1.0)), clamp(a * uAlpha, 0.0, 0.9));
}`;

const BUBBLE_VERT = `varying vec3 vN; varying vec3 vV; varying vec3 vObj;
void main(){ vObj = position; vec4 mv = modelViewMatrix * vec4(position, 1.0); vN = normalize(normalMatrix * normal); vV = normalize(-mv.xyz); gl_Position = projectionMatrix * mv; }`;

const BUBBLE_FRAG = `
uniform float uTime; uniform vec4 uP; uniform vec3 uColor; uniform vec3 uHot; uniform float uAlpha; uniform float uMotion; uniform float uSeed;
varying vec3 vN; varying vec3 vV; varying vec3 vObj;
${NOISE_GLSL}
void main(){
  // uP = (crack 0..1, gatedCast, shatter flash, alpha)
  float fres = pow(1.0 - abs(dot(normalize(vN), normalize(vV))), 2.2);
  vec2 q = vec2(atan(vObj.z, vObj.x) * 1.6, vObj.y * 2.6) + uSeed;
  float c = abs(noise(q * 2.2) - 0.5) + abs(noise(q * 4.7 + 3.1) - 0.5) * 0.5;
  float reveal = step(noise(q * 0.9 + 7.0), uP.x * 1.15);
  float crackL = (1.0 - smoothstep(0.015, 0.05 + 0.04 * uP.x, c)) * reveal;
  float shimmer = 0.85 + 0.15 * sin(uTime * 3.0 + vObj.y * 6.0) * uMotion;
  float gated = uP.y * (0.6 + 0.4 * sin(uTime * 5.0));
  float a = fres * (0.28 + 0.12 * gated) * shimmer + crackL * 0.55 + uP.z * 0.35 * (fres + crackL);
  vec3 col = mix(uColor, uHot, clamp(crackL + uP.z, 0.0, 1.0));
  gl_FragColor = vec4(min(col, vec3(1.0)), clamp(a * uAlpha * uP.w, 0.0, 0.8));
}`;

const v3 = (c: readonly number[]): THREE.Vector3 => new THREE.Vector3(c[0], c[1], c[2]);
const setV3 = (u: THREE.IUniform, c: readonly number[]): void => void (u.value as THREE.Vector3).set(c[0]!, c[1]!, c[2]!);

/** Colours (linear, below bloom). Pip / hot colours are the only pale ones and stay small. */
const COL = {
  soakFill: HAZARD.soak,
  soakRim: HAZARD.soak,
  soakPip: HAZARD.soakPip,
  beam: HAZARD.beamEdge,
  beamCore: HAZARD.beamCore,
  beamHot: [0.98, 0.8, 0.45],
  pool: HAZARD.poison,
  poolRim: HAZARD.poisonRim,
  poolHot: [0.72, 0.82, 0.3],
  wave: HAZARD.avoid,
  waveRim: HAZARD.avoidRim,
  waveHot: [0.98, 0.72, 0.4],
  ring: HAZARD.avoid,
  ringRim: HAZARD.avoidRim,
  ringHot: HAZARD.deadlyRim,
  tether: HAZARD.tether,
  tetherHot: [0.98, 0.7, 0.55],
  fixate: HAZARD.tether,
  fixateRim: HAZARD.deadlyRim,
  lane: HAZARD.avoid,
  laneRim: HAZARD.avoidRim,
  laneHot: [0.98, 0.72, 0.4],
  laneDeadly: HAZARD.deadly,
  laneDeadlyRim: HAZARD.deadlyRim,
  mover: HAZARD.avoid,
  moverRim: HAZARD.avoidRim,
  moverHot: HAZARD.boneCore,
  bubble: [0.92, 0.58, 0.18],
  bubbleHot: [1.0, 0.86, 0.6],
} as const;

class GroundSlot {
  readonly group = new THREE.Group();
  readonly mesh: THREE.Mesh;
  readonly mat: THREE.ShaderMaterial;
  readonly u: Record<string, THREE.IUniform>;

  constructor(geo: THREE.PlaneGeometry, mode: number, seed: number) {
    this.mat = new THREE.ShaderMaterial({
      uniforms: {
        uTime: U.uTime,
        uMode: { value: mode },
        uShape: { value: 0 },
        uHalf: { value: new THREE.Vector2(1, 1) },
        uA: { value: 1 },
        uB: { value: 0 },
        uP: { value: new THREE.Vector4() },
        uQ: { value: new THREE.Vector4() },
        uColor: { value: new THREE.Vector3() },
        uRim: { value: new THREE.Vector3() },
        uHot: { value: new THREE.Vector3() },
        uAlpha: { value: 0 },
        uMotion: { value: 1 },
        uSeed: { value: seed },
      },
      vertexShader: GROUND_VERT,
      fragmentShader: GROUND_FRAG,
      transparent: true,
      depthWrite: false,
      side: THREE.DoubleSide,
      polygonOffset: true,
      polygonOffsetFactor: -3,
      polygonOffsetUnits: -3,
    });
    this.u = this.mat.uniforms;
    this.mesh = new THREE.Mesh(geo, this.mat);
    this.mesh.rotation.x = -Math.PI / 2;
    this.mesh.renderOrder = 4;
    this.group.add(this.mesh);
    this.group.visible = false;
  }

  colors(color: readonly number[], rim: readonly number[], hot: readonly number[]): void {
    setV3(this.u['uColor']!, color);
    setV3(this.u['uRim']!, rim);
    setV3(this.u['uHot']!, hot);
  }

  /** Place as a disc (radius a, inner b) or a box (half-length a, half-width b). */
  place(shape: 0 | 1, x: number, y: number, facing: number, a: number, b: number, lift = 0.05): void {
    const hx = a;
    const hy = shape === 0 ? a : b;
    this.u['uShape']!.value = shape;
    this.u['uA']!.value = a;
    this.u['uB']!.value = b;
    (this.u['uHalf']!.value as THREE.Vector2).set(hx, hy);
    this.mesh.scale.set(Math.max(0.01, hx), Math.max(0.01, hy), 1);
    this.group.position.set(x, groundY(x, y) + lift, -y);
    this.group.rotation.y = facing;
  }

  placeArea(m: AreaMark, lift?: number): void {
    this.place(m.shape, m.x, m.y, m.facing, m.a, m.b, lift);
  }

  setP(x: number, y: number, z: number, w: number): void {
    (this.u['uP']!.value as THREE.Vector4).set(x, y, z, w);
  }

  dispose(): void {
    this.mat.dispose();
  }
}

class BubbleSlot {
  readonly mesh: THREE.Mesh;
  readonly mat: THREE.ShaderMaterial;
  readonly state: Bubble = newBubble();
  /** Last placed radius / height (the shatter keeps expanding from them). */
  r = 1;
  h = 1;

  constructor(geo: THREE.SphereGeometry, seed: number) {
    this.mat = new THREE.ShaderMaterial({
      uniforms: {
        uTime: U.uTime,
        uP: { value: new THREE.Vector4() },
        uColor: { value: v3(COL.bubble) },
        uHot: { value: v3(COL.bubbleHot) },
        uAlpha: { value: 1 },
        uMotion: { value: 1 },
        uSeed: { value: seed },
      },
      vertexShader: BUBBLE_VERT,
      fragmentShader: BUBBLE_FRAG,
      transparent: true,
      depthWrite: false,
    });
    this.mesh = new THREE.Mesh(geo, this.mat);
    this.mesh.renderOrder = 7;
    this.mesh.visible = false;
  }
}

class Overhead {
  readonly mesh: THREE.Mesh;
  readonly mat = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, depthWrite: false });
  constructor(geo: THREE.ConeGeometry) {
    this.mesh = new THREE.Mesh(geo, this.mat);
    this.mesh.renderOrder = 8;
    this.mesh.visible = false;
  }
}

/** Pool sizes (max simultaneous marks per kind). */
export const MECH_POOL = { soak: 8, beam: 4, pool: 10, wave: 16, ring: 6, link: 10, bubble: 6, overhead: 8, lane: 4, mover: 8, cone: 4 } as const;

export class MechanicsFx {
  readonly group = new THREE.Group();
  /** Effects intensity (settings.fxIntensity). */
  intensity = 1;

  private readonly plane = new THREE.PlaneGeometry(2, 2);
  private readonly dome = new THREE.SphereGeometry(1, 24, 12, 0, Math.PI * 2, 0, Math.PI / 2);
  private readonly cone = new THREE.ConeGeometry(0.26, 0.5, 4).rotateX(Math.PI);

  private readonly soaks = this.ground(MECH_POOL.soak, MODE.soak);
  private readonly beams = this.ground(MECH_POOL.beam, MODE.beam);
  private readonly pools = this.ground(MECH_POOL.pool, MODE.pool);
  private readonly waves = this.ground(MECH_POOL.wave, MODE.wave);
  private readonly rings = this.ground(MECH_POOL.ring, MODE.ring);
  private readonly links = this.ground(MECH_POOL.link, MODE.link);
  private readonly lanes = this.ground(MECH_POOL.lane, MODE.lane);
  private readonly movers = this.ground(MECH_POOL.mover, MODE.mover);
  private readonly cones = this.ground(MECH_POOL.cone, MODE.cone);
  private readonly bubbles: BubbleSlot[] = [];
  private readonly heads: Overhead[] = [];

  readonly tSoak = new FadeTracker(MECH_POOL.soak, 0.15, 0.2);
  readonly tBeam = new FadeTracker(MECH_POOL.beam, 0.1, 0.2);
  readonly tPool = new FadeTracker(MECH_POOL.pool, 0.25, 0.4);
  readonly tWave = new FadeTracker(MECH_POOL.wave, 0.12, 0.15);
  readonly tRing = new FadeTracker(MECH_POOL.ring, 0.15, 0.25);
  readonly tLink = new FadeTracker(MECH_POOL.link, 0.12, 0.2);
  readonly tBubble = new FadeTracker(MECH_POOL.bubble, 0.2, 0.5);
  readonly tHead = new FadeTracker(MECH_POOL.overhead, 0.15, 0.2);
  readonly tLane = new FadeTracker(MECH_POOL.lane, 0.1, 0.15);
  readonly tMover = new FadeTracker(MECH_POOL.mover, 0.15, 0.25);
  readonly tCone = new FadeTracker(MECH_POOL.cone, 0.12, 0.2);

  private readonly soakL: SoakLook = { pips: 1, filled: 0, satisfied: false, urgency: 0 };
  private readonly cd: CountdownLook = { sweep: 1, pulseHz: 1, urgent: false };
  private readonly tl: TetherLook = { width: 0.3, strain: 0 };
  /** Overhead marker tone per slot: 0 avoid, 1 deadly, 2 fixate. Spin speed in `spin`. */
  private readonly headTone = new Float32Array(MECH_POOL.overhead);
  private readonly headSpin = new Float32Array(MECH_POOL.overhead);
  private warmOn = false;

  constructor() {
    for (let i = 0; i < MECH_POOL.bubble; i++) {
      const b = new BubbleSlot(this.dome, i * 1.73);
      this.bubbles.push(b);
      this.group.add(b.mesh);
    }
    for (let i = 0; i < MECH_POOL.overhead; i++) {
      const h = new Overhead(this.cone);
      this.heads.push(h);
      this.group.add(h.mesh);
    }
    this.soaks.forEach((s) => s.colors(COL.soakFill, COL.soakRim, COL.soakPip));
    this.beams.forEach((s) => s.colors(COL.beam, COL.beamCore, COL.beamHot));
    this.pools.forEach((s) => s.colors(COL.pool, COL.poolRim, COL.poolHot));
    this.waves.forEach((s) => s.colors(COL.wave, COL.waveRim, COL.waveHot));
    this.rings.forEach((s) => s.colors(COL.ring, COL.ringRim, COL.ringHot));
    this.movers.forEach((s) => s.colors(COL.mover, COL.moverRim, COL.moverHot));
  }

  private ground(n: number, mode: number): GroundSlot[] {
    const out: GroundSlot[] = [];
    for (let i = 0; i < n; i++) {
      const s = new GroundSlot(this.plane, mode, (i * 1.37 + mode * 2.1) % 10);
      out.push(s);
      this.group.add(s.group);
    }
    return out;
  }

  /**
   * One frame. `units` are the interpolated render units; `heightOf` gives a unit's model
   * height (overhead markers). `dt` in seconds (fx time, hit-stop aware).
   */
  update(m: MechanicsFrame | undefined, units: ReadonlyMap<UnitId, RenderUnit>, heightOf: (id: UnitId) => number, dt: number): void {
    if (this.warmOn) return;
    const info = infoAlpha(this.intensity);
    const motion = flourish(this.intensity);

    // ---- soaks ----
    this.tSoak.begin();
    if (m)
      for (let i = 0; i < m.nSoaks; i++) {
        const k = m.soaks[i]!;
        const si = this.tSoak.touch(k.id);
        if (si < 0) continue;
        const s = this.soaks[si]!;
        this.tSoak.slots[si]!.a = k.progress;
        s.placeArea(k, 0.06);
        const l = soakLook(k.need, k.inside, k.progress, this.soakL);
        s.setP(l.pips, l.filled, k.progress, l.urgency);
        (s.u['uQ']!.value as THREE.Vector4).x = l.satisfied ? 1 : 0;
      }
    markResolved(this.tSoak, 0.97);
    this.tSoak.end(dt);
    this.show(this.soaks, this.tSoak, info, motion);

    // ---- beams ----
    this.tBeam.begin();
    if (m)
      for (let i = 0; i < m.nBeams; i++) {
        const b = m.beams[i]!;
        const si = this.tBeam.touch(b.id);
        if (si < 0) continue;
        // Follow the caster's interpolated position so the beam doesn't stutter at 20 Hz.
        const anchor = b.anchorId ? units.get(b.anchorId) : undefined;
        const half = b.length * 0.5;
        const ox = anchor ? anchor.pos.x : b.x - Math.cos(b.facing) * half;
        const oy = anchor ? anchor.pos.y : b.y - Math.sin(b.facing) * half;
        this.beams[si]!.place(1, ox + Math.cos(b.facing) * half, oy + Math.sin(b.facing) * half, b.facing, half, b.width * 0.5, 0.07);
        this.beams[si]!.setP(b.length, 0, 0, 0);
      }
    this.tBeam.end(dt);
    this.show(this.beams, this.tBeam, info, motion);

    // ---- growing pools ----
    this.tPool.begin();
    if (m)
      for (let i = 0; i < m.nPools; i++) {
        const p = m.pools[i]!;
        const si = this.tPool.touch(p.id);
        if (si < 0) continue;
        const s = this.pools[si]!;
        s.place(0, p.x, p.y, 0, p.max + 0.2, 0, 0.045);
        s.setP(p.a, p.max, p.growth, p.life);
      }
    this.tPool.end(dt);
    this.show(this.pools, this.tPool, info, motion);

    // ---- waves ----
    this.tWave.begin();
    if (m)
      for (let i = 0; i < m.nWaves; i++) {
        const w = m.waves[i]!;
        const si = this.tWave.touch(w.id);
        if (si < 0) continue;
        const s = this.waves[si]!;
        this.tWave.slots[si]!.a = w.resolvesInMs;
        s.placeArea(w, 0.06);
        const ph = wavePhase(w.resolvesInMs, w.next);
        s.setP(ph, waveFront(w.resolvesInMs), 0, 0);
      }
    // A band that vanished right at its resolve time went off: flash it.
    for (const sl of this.tWave.slots) if (sl.state !== SlotState.Free && sl.state !== SlotState.Leaving && !sl.seen && sl.a < 200) sl.resolved = true;
    this.tWave.end(dt);
    for (let i = 0; i < this.waves.length; i++) {
      const sl = this.tWave.slots[i]!;
      if (sl.state === SlotState.Leaving && sl.resolved) this.waves[i]!.setP(WavePhase.Fired, 1, sl.alpha, 0);
    }
    this.show(this.waves, this.tWave, info, motion);

    // ---- player rings + overhead markers ----
    this.tRing.begin();
    this.tHead.begin();
    if (m)
      for (let i = 0; i < m.nUnits; i++) {
        const k = m.units[i]!;
        const u = units.get(k.unitId);
        if (!u || !u.alive) continue;
        const si = this.tRing.touch(k.unitId);
        if (si < 0) continue;
        const remaining = Number.isFinite(k.remainingMs) ? k.remainingMs : k.durationMs;
        const cd = countdownLook(remaining, k.durationMs, this.cd);
        const explode = k.kind === 'explode';
        const r = growingRadius(k.radius, cd.sweep, k.grows);
        const s = this.rings[si]!;
        s.place(0, u.pos.x, u.pos.y, 0, r, 0, 0.065);
        s.setP(explode || k.grows ? cd.sweep : 1, explode ? cd.pulseHz : 0.6, explode && cd.urgent ? 1 : 0, explode ? 2 : k.kind === 'isolate' ? 1 : 0);
        const hi = this.tHead.touch(k.unitId);
        if (hi >= 0) {
          this.headTone[hi] = explode && cd.urgent ? 1 : 0;
          this.headSpin[hi] = explode ? (cd.urgent ? 9 : 4) : 2;
          this.placeHead(hi, u, heightOf(u.id));
        }
      }
    this.tRing.end(dt);
    this.show(this.rings, this.tRing, info, motion);

    // ---- fixate / tether lines ----
    this.tLink.begin();
    if (m)
      for (let i = 0; i < m.nLinks; i++) {
        const k = m.links[i]!;
        const a = units.get(k.fromId);
        const b = units.get(k.toId);
        if (!a || !b) continue;
        const si = this.tLink.touch(k.id);
        if (si < 0) continue;
        const fix = k.kind === 'fixate';
        const look = tetherLook(k.tension, fix, this.tl);
        const dx = b.pos.x - a.pos.x;
        const dy = b.pos.y - a.pos.y;
        const len = Math.max(0.1, Math.hypot(dx, dy));
        const s = this.links[si]!;
        this.tLink.slots[si]!.a = k.tension;
        s.place(1, (a.pos.x + b.pos.x) * 0.5, (a.pos.y + b.pos.y) * 0.5, Math.atan2(dy, dx), len * 0.5, look.width * 0.5, 0.08);
        s.setP(look.strain, fix ? 0 : 1, 0, len);
        if (fix) s.colors(COL.fixate, COL.fixateRim, COL.tetherHot);
        else s.colors(COL.tether, COL.tether, COL.tetherHot);
        if (fix) {
          const hi = this.tHead.touch(k.toId);
          if (hi >= 0 && this.headTone[hi] !== 1) {
            this.headTone[hi] = 2;
            this.headSpin[hi] = 3;
            this.placeHead(hi, b, heightOf(b.id));
          }
        }
      }
    // A tether that vanished at full stretch snapped: flash.
    markResolved(this.tLink, 0.95);
    this.tLink.end(dt);
    for (let i = 0; i < this.links.length; i++) {
      const sl = this.tLink.slots[i]!;
      if (sl.state === SlotState.Leaving && sl.resolved) (this.links[i]!.u['uP']!.value as THREE.Vector4).z = sl.alpha;
    }
    this.show(this.links, this.tLink, info, motion);

    // ---- overhead markers ----
    this.tHead.end(dt);
    for (let i = 0; i < this.heads.length; i++) {
      const sl = this.tHead.slots[i]!;
      const h = this.heads[i]!;
      h.mesh.visible = sl.state !== SlotState.Free;
      if (!h.mesh.visible) continue;
      const tone = this.headTone[i]!;
      const c = tone === 1 ? HAZARD.deadlyRim : tone === 2 ? HAZARD.tether : HAZARD.avoidRim;
      h.mat.color.setRGB(c[0], c[1], c[2]);
      h.mat.opacity = 0.9 * sl.alpha * info;
      h.mesh.rotation.y += dt * this.headSpin[i]! * Math.max(0.3, motion);
    }

    // ---- charge lanes ----
    this.tLane.begin();
    if (m)
      for (let i = 0; i < m.nLanes; i++) {
        const k = m.lanes[i]!;
        const si = this.tLane.touch(k.id);
        if (si < 0) continue;
        const s = this.lanes[si]!;
        s.placeArea(k, 0.065);
        s.setP(k.progress, k.a * 2, k.deadly ? 1 : 0, 0);
        if (k.deadly) s.colors(COL.laneDeadly, COL.laneDeadlyRim, COL.laneHot);
        else s.colors(COL.lane, COL.laneRim, COL.laneHot);
      }
    this.tLane.end(dt);
    this.show(this.lanes, this.tLane, info, motion);

    // ---- moving hazards (axes, vortices, rolling waves) ----
    this.tMover.begin();
    if (m)
      for (let i = 0; i < m.nMovers; i++) {
        const k = m.movers[i]!;
        const si = this.tMover.touch(k.id);
        if (si < 0) continue;
        const s = this.movers[si]!;
        const speed = Math.hypot(k.vx, k.vy);
        const reach = k.radius * 2.6 + 0.6;
        // Quad centred on the hazard, +x along its travel (sim positions are at 20 Hz; the
        // base decal snaps the same way, so the overlay stays glued to it).
        s.place(0, k.x, k.y, speed > 0.01 ? Math.atan2(k.vy, k.vx) : 0, reach, 0, 0.055);
        s.setP(k.radius, k.spin, speed, 0);
      }
    this.tMover.end(dt);
    this.show(this.movers, this.tMover, info, motion);

    // ---- untelegraphed frontal casts (Bloodroar's safe wedge) ----
    this.tCone.begin();
    if (m)
      for (let i = 0; i < m.nCones; i++) {
        const k = m.cones[i]!;
        const u = units.get(k.unitId);
        if (!u || !u.alive) continue;
        const si = this.tCone.touch(k.unitId);
        if (si < 0) continue;
        const s = this.cones[si]!;
        s.place(0, u.pos.x, u.pos.y, u.facing, k.range, 0, 0.05);
        s.setP(k.half, k.progress, k.half > Math.PI / 2 ? 1 : 0, k.deadly ? 1 : 0);
        if (k.deadly) s.colors(COL.laneDeadly, COL.laneDeadlyRim, COL.laneHot);
        else s.colors(COL.lane, COL.laneRim, COL.laneHot);
      }
    this.tCone.end(dt);
    this.show(this.cones, this.tCone, info, motion);

    // ---- shield bubbles ----
    this.tBubble.begin();
    if (m)
      for (let i = 0; i < m.nShields; i++) {
        const k = m.shields[i]!;
        const u = units.get(k.unitId);
        if (!u) continue;
        const si = this.tBubble.touch(k.unitId);
        if (si < 0) continue;
        const b = this.bubbles[si]!;
        stepBubble(b.state, k.absorb, dt);
        this.tBubble.slots[si]!.a = k.gatedCast ? 1 : 0;
        b.r = Math.max(1.2, u.radius * 1.5);
        b.h = Math.max(1.6, heightOf(u.id) * 0.75);
        b.mesh.position.set(u.pos.x, groundY(u.pos.x, u.pos.y), -u.pos.y);
      }
    for (let i = 0; i < this.bubbles.length; i++) {
      const sl = this.tBubble.slots[i]!;
      // Untouched live slots: the shield aura is gone this frame.
      if (sl.state !== SlotState.Free && !sl.seen) stepBubble(this.bubbles[i]!.state, null, dt);
    }
    this.tBubble.end(dt);
    for (let i = 0; i < this.bubbles.length; i++) {
      const sl = this.tBubble.slots[i]!;
      const b = this.bubbles[i]!;
      const st = b.state;
      const visible = sl.state !== SlotState.Free && st.state !== BubbleState.Gone;
      b.mesh.visible = visible;
      if (!visible) continue;
      const flash = shatterFlash(st);
      const alpha = st.state === BubbleState.Shattered ? 1 : sl.alpha;
      (b.mat.uniforms['uP']!.value as THREE.Vector4).set(st.crack, sl.a, flash * motion, alpha);
      b.mat.uniforms['uAlpha']!.value = info;
      b.mat.uniforms['uMotion']!.value = motion;
      // Shatter: the shell bursts outward while it flashes and fades.
      const k = st.state === BubbleState.Shattered ? 1 + 0.25 * (1 - shatterFlash(st)) : 1;
      b.mesh.scale.set(b.r * k, b.h * k, b.r * k);
    }
  }

  private placeHead(i: number, u: RenderUnit, height: number): void {
    const h = this.heads[i]!;
    const bob = Math.sin(U.uTime.value * 3 + i) * 0.08;
    h.mesh.position.set(u.pos.x, groundY(u.pos.x, u.pos.y) + height + 1.35 + bob, -u.pos.y);
  }

  private show(slots: GroundSlot[], tr: FadeTracker, info: number, motion: number): void {
    for (let i = 0; i < slots.length; i++) {
      const sl = tr.slots[i]!;
      const s = slots[i]!;
      const on = sl.state !== SlotState.Free;
      s.group.visible = on;
      if (!on) continue;
      s.u['uAlpha']!.value = sl.alpha * info;
      s.u['uMotion']!.value = motion;
    }
  }

  /** Prewarm: show one slot of each kind (far away) so their programs compile up front. */
  warm(on: boolean, at: { x: number; y: number } = { x: 5000, y: 5000 }): void {
    this.warmOn = on;
    const firsts = [this.soaks[0]!, this.beams[0]!, this.pools[0]!, this.waves[0]!, this.rings[0]!, this.links[0]!, this.lanes[0]!, this.movers[0]!, this.cones[0]!];
    for (const s of firsts) {
      if (on) s.place(0, at.x, at.y, 0, 1, 0);
      s.group.visible = on;
    }
    this.bubbles[0]!.mesh.visible = on;
    this.heads[0]!.mesh.visible = on;
    if (on) {
      this.bubbles[0]!.mesh.position.set(at.x, 0, -at.y);
      this.heads[0]!.mesh.position.set(at.x, 0, -at.y);
    }
  }

  /** Visible draw count (debug / perf budget). */
  visibleCount(): number {
    let n = 0;
    this.group.traverse((o) => {
      if ((o as THREE.Mesh).isMesh && o.visible && o.parent?.visible !== false) n++;
    });
    return n;
  }

  clear(): void {
    for (const t of [this.tSoak, this.tBeam, this.tPool, this.tWave, this.tRing, this.tLink, this.tBubble, this.tHead, this.tLane, this.tMover, this.tCone]) t.clear();
    for (const b of this.bubbles) {
      b.state.state = BubbleState.Gone;
      b.mesh.visible = false;
    }
    for (const h of this.heads) h.mesh.visible = false;
    for (const s of [...this.soaks, ...this.beams, ...this.pools, ...this.waves, ...this.rings, ...this.links, ...this.lanes, ...this.movers, ...this.cones]) s.group.visible = false;
  }

  dispose(): void {
    this.group.removeFromParent();
    for (const s of [...this.soaks, ...this.beams, ...this.pools, ...this.waves, ...this.rings, ...this.links, ...this.lanes, ...this.movers, ...this.cones]) s.dispose();
    for (const b of this.bubbles) b.mat.dispose();
    for (const h of this.heads) h.mat.dispose();
    this.plane.dispose();
    this.dome.dispose();
    this.cone.dispose();
  }
}

/** Slots that vanished this frame with their tracked value (`a`) at/above `at` went off. */
function markResolved(tr: FadeTracker, at: number): void {
  for (const sl of tr.slots) if (sl.state !== SlotState.Free && sl.state !== SlotState.Leaving && !sl.seen && sl.a >= at) sl.resolved = true;
}
